<template>
    <div class="vrm-modal mfp-with-anim">
        <div v-if="!success">
            <div class="vrm-modal__header">
                <div class="vrm-modal__title">
                    {{
                        $t('accesstokens.add_token_title', {
                            i18nFallback: 'Add a personal access token',
                        })
                    }}
                </div>
            </div>
            <vrm-form class="vrm-modal__content" @submit="onSubmit">
                <p
                    v-html="
                        $t('accesstokens.add_token_copy', {
                            i18nFallback:
                                'You can use personal access tokens to authenticate against VRM over HTTP. They are the only accepted password when you have Two-Factor Authentication (2FA) enabled.',
                        })
                    "
                ></p>

                <div v-if="hasError" class="token-error vrm-form_input-errors">
                    <span class="sg-input-error">
                        {{ error }}
                    </span>
                </div>

                <div class="vrm-form__fieldset-item">
                    <vrm-text-field
                        v-model="name"
                        class="sg-element"
                        type="text"
                        required
                        :disabled="loading"
                        :checks="[CANNOT_CONTAIN_URL]"
                        :title="$t('accesstokens.name', { i18nFallback: 'Name' })"
                    ></vrm-text-field>
                </div>

                <p>
                    {{
                        $t('accesstokens.expiry_copy', {
                            i18nFallback:
                                'Optionally, you can set an expiration date for the access token. If you leave this empty the access token will be valid indefinitely until you manually remove it.',
                        })
                    }}
                </p>

                <div class="vrm-form__fieldset-item">
                    <div class="sg-textfield-label">
                        {{ $t('accesstokens.expiry') }}
                    </div>
                    <input
                        v-model="expiry"
                        type="date"
                        class="picker__input picker__input sg-element"
                        :min="minDate"
                    />
                </div>

                <div class="sg-spread">
                    <button
                        type="reset"
                        class="sg-btn sg-site"
                        :disabled="loading"
                        @click="onReset"
                    >
                        {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
                    </button>
                    <button type="submit" class="sg-btn sg-blue" :disabled="loading">
                        <span v-if="loading">Loading...</span>
                        <span v-else>
                            {{
                                $t('accesstokens.create_token', {
                                    i18nFallback: 'Create token',
                                })
                            }}
                        </span>
                    </button>
                </div>
            </vrm-form>
            <modal-close-handle />
        </div>

        <div v-if="success">
            <div class="vrm-modal__header">
                <div class="vrm-modal__title">
                    {{
                        $t('accesstokens.access_token_modal_title', {
                            i18nFallback: 'Access token generated successfully',
                        })
                    }}
                </div>
            </div>

            <vrm-form class="vrm-modal__content">
                <p>
                    {{
                        $t('accesstokens.access_token_modal_copy', {
                            i18nFallback:
                                'Please save this token carefully. After you close this window, there is no way to retrieve it.',
                        })
                    }}
                </p>

                <div class="vrm-form__fieldset-item">
                    <vrm-share-box :content="token" />
                </div>

                <div class="sg-spread">
                    <button
                        type="reset"
                        class="sg-btn sg-site"
                        :disabled="loading"
                        @click="onComplete"
                    >
                        {{ $t('common.close', { i18nFallback: 'Close' }) }}
                    </button>
                </div>
            </vrm-form>

            <modal-close-handle />
        </div>
    </div>
</template>
<style scoped>
.token-error {
    padding-bottom: 1.5rem;
}

.vrm-form__fieldset-item {
    margin-top: 20px; /* Adjust as needed */
}
</style>
<script>
import { Component, Vue } from 'vue-property-decorator';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import { ngInject } from '@/vue/decorators/angular-interop';
import VrmAvatarStack from '@/vue/components/shared/VrmAvatarStack';
import VrmAvatar from '@/vue/components/shared/VrmAvatar';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmForm from '@/vue/components/forms/VrmForm';
import VrmTextField from '@/vue/components/forms/VrmTextField';
import VrmTextArea from '@/vue/components/forms/VrmTextArea';
import VrmShareBox from '@/vue/components/shared/VrmShareBox';
import { CANNOT_CONTAIN_URL } from '@/vue/constants/validation-checks';
import {
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATIONS,
    VrmNotification,
} from '@/vue/store/modules/notifications';

@Component({
    components: {
        VrmTextArea,
        VrmTextField,
        VrmForm,
        VrmAvatar,
        VrmAvatarStack,
        VrmDropdown,
        ModalCloseHandle,
        VrmShareBox,
    },
})
@AngularLocaleBundle('common')
@AngularLocaleBundle('accesstokens')
export default class AccessTokenCreateModal extends Vue {
    CANNOT_CONTAIN_URL = CANNOT_CONTAIN_URL;
    name = '';
    loading = false;
    token = null;
    error = null;
    expiry = null;

    /** @type AccessTokensService */
    @ngInject()
    AccessTokensService;

    get minDate() {
        const today = new Date();

        // Add 1 to month since it starts at 0.
        return (
            today.getFullYear() +
            '-' +
            this._formatDatePart(today.getMonth() + 1) +
            '-' +
            this._formatDatePart(today.getDate())
        );
    }

    get success() {
        return this.token != null && this.loading === false;
    }

    get hasError() {
        return this.error != null;
    }

    async onSubmit() {
        // TODO(rick): Move create logic to a separate AccessTokenService?
        try {
            this.loading = true;
            this.error = null;

            let expiration = undefined;
            if (this.expiry) {
                const date = new Date(this.expiry);
                date.setHours(23);
                date.setMinutes(59);

                const utcDate = new Date(date.toUTCString());

                expiration = utcDate.getTime() / 1000;
            }

            let resp = await this.AccessTokensService.createToken(this.name, expiration);
            if (resp.success) {
                // Successfully created a token
                this.token = resp.token;

                this.$store.dispatch(NOTIFICATIONS.ADD, {
                    notification: new VrmNotification({
                        type: NOTIFICATION_TYPE_SUCCESS,
                        title: this.$t('accesstokens.access_token_modal_title', {
                            i18nFallback: 'Access token generated successfully.',
                        }),
                    }),
                });
            } else if ('name' in resp.errors) {
                // An error occurred
                this.error = resp.errors.name;
            } else {
                // Some unknown error occurred.
                this.error = this.$t('core.unknown_error', {
                    i18nFallback: 'Something went wrong. Please try again later.',
                });
            }
        } finally {
            this.loading = false;
        }
    }

    /**
     * Make sure the date parts are always formatted as 2 digits.
     *
     * E.g. 2023-01-01
     *
     * @param part
     * @returns {string}
     * @private
     */
    _formatDatePart(part) {
        return part < 10 ? `0${part}` : `${part}`;
    }

    onReset() {
        this.$emit('close', false);
    }

    onComplete() {
        this.$emit('close', true);
    }
}
</script>
